import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Checkbox } from "../ui/checkbox";
import { BookUser, CheckCircle2, Send, Trash2, Phone, Merge, X, Pencil } from "lucide-react";

// Merge Name Picker Modal
function MergeNameModal({ names, onConfirm, onClose }) {
  const uniqueNames = [...new Set(names)];
  const [customName, setCustomName] = useState(uniqueNames[0] || "");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <Merge className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-bold text-slate-900">Merge Contacts</h3>
        </div>

        <p className="text-sm text-slate-600 mb-4">Choose a name for the merged contact:</p>

        {/* Name options */}
        <div className="space-y-2 mb-4">
          {uniqueNames.map((name) => (
            <button
              key={name}
              onClick={() => setCustomName(name)}
              className={`w-full text-left p-3 rounded-lg border transition-all text-sm font-medium ${
                customName === name
                  ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                  : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Custom name input */}
        <div className="flex items-center gap-2 mb-5">
          <Pencil className="w-4 h-4 text-slate-400 shrink-0" />
          <Input
            data-testid="merge-custom-name"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Or type a custom name"
            className="text-sm"
          />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-merge-name-btn"
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => onConfirm(customName.trim())}
            disabled={!customName.trim()}
          >
            <Merge className="w-4 h-4 mr-1" />
            Merge
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ContactsList({ 
  contacts, 
  postcodes, 
  onAddFromContact, 
  onDeleteContact,
  onMergeContacts, 
}) {
  const [selected, setSelected] = useState(new Set());
  const [showNamePicker, setShowNamePicker] = useState(false);

  const sortedContacts = useMemo(() => 
    [...contacts].sort((a, b) => 
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    ), [contacts]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMergeClick = () => {
    const selectedContacts = contacts.filter((c) => selected.has(c.id));
    const uniqueNames = [...new Set(selectedContacts.map((c) => c.name))];
    
    if (uniqueNames.length === 1) {
      // Same name — merge directly, no prompt needed
      onMergeContacts([...selected], uniqueNames[0]);
      setSelected(new Set());
    } else {
      // Different names — show name picker
      setShowNamePicker(true);
    }
  };

  const handleNameConfirm = (name) => {
    onMergeContacts([...selected], name);
    setSelected(new Set());
    setShowNamePicker(false);
  };

  const selectedNames = useMemo(() => {
    return contacts.filter((c) => selected.has(c.id)).map((c) => c.name);
  }, [selected, contacts]);

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <BookUser className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p>No saved contacts</p>
        <p className="text-sm mt-1">Contacts are automatically saved when you add postcodes</p>
      </div>
    );
  }

  return (
    <div>
      {/* Merge toolbar */}
      {selected.size >= 2 && (
        <div className="flex items-center justify-between mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <p className="text-sm font-medium text-indigo-700">
            {selected.size} contacts selected
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              data-testid="confirm-merge-btn"
              onClick={handleMergeClick}
              className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
            >
              <Merge className="w-3.5 h-3.5" />
              Merge Selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelected(new Set())}
              className="text-xs"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      <ScrollArea className="h-[500px] pr-4 custom-scrollbar">
          {sortedContacts.map((contact) => {
            const contactPostcodes = contact.postcodes?.length
              ? contact.postcodes
              : [contact.postcode];
            const isSelected = selected.has(contact.id);

            const isInRoute = contactPostcodes.some((pc) =>
              postcodes.some(
                (p) =>
                  p.postcode.toUpperCase() === pc.toUpperCase() &&
                  p.name.toUpperCase() === contact.name.toUpperCase() &&
                  !p.completed
              )
            );

            return (
              <div
                key={contact.id}
                className={`p-4 rounded-xl border mb-3 transition-colors ${
                  isSelected
                    ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200"
                    : isInRoute
                    ? "bg-blue-50 border-blue-200"
                    : "bg-white border-slate-100 hover:shadow-md"
                }`}
                data-testid={`contact-item-${contact.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      data-testid={`select-contact-${contact.id}`}
                      onCheckedChange={() => toggleSelect(contact.id)}
                      className="h-5 w-5 shrink-0"
                    />
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                        isInRoute ? "bg-blue-500" : "bg-purple-500"
                      }`}
                    >
                      {isInRoute ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <BookUser className="w-5 h-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-semibold ${isInRoute ? "text-blue-900" : "text-slate-900"}`}>
                        {contact.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {contactPostcodes.map((pc, i) => (
                          <span key={pc + i} className="text-sm text-slate-500 font-mono">
                            {pc}
                            {i < contactPostcodes.length - 1 && (
                              <span className="text-slate-300 ml-2">|</span>
                            )}
                          </span>
                        ))}
                        {contact.phone && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" />{contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {contactPostcodes.length === 1 ? (
                      isInRoute ? (
                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-3 py-1.5 rounded-full">
                          In Route
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`add-from-contact-${contact.id}`}
                          onClick={() => onAddFromContact({ ...contact, postcode: contactPostcodes[0] })}
                          className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <Send className="w-3 h-3" />Add to Route
                        </Button>
                      )
                    ) : (
                      <div className="flex flex-col gap-1">
                        {contactPostcodes.map((pc) => {
                          const pcInRoute = postcodes.some(
                            (p) =>
                              p.postcode.toUpperCase() === pc.toUpperCase() &&
                              p.name.toUpperCase() === contact.name.toUpperCase() &&
                              !p.completed
                          );
                          return pcInRoute ? (
                            <span key={pc} className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-center">
                              {pc} — In Route
                            </span>
                          ) : (
                            <Button
                              key={pc}
                              variant="outline"
                              size="sm"
                              onClick={() => onAddFromContact({ ...contact, postcode: pc })}
                              className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50 text-xs h-7 px-2"
                            >
                              <Send className="w-3 h-3" />{pc}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-contact-${contact.id}`}
                      onClick={() => onDeleteContact(contact)}
                      className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
      </ScrollArea>

      {/* Merge Name Picker Modal */}
      <AnimatePresence>
        {showNamePicker && (
          <MergeNameModal
            names={selectedNames}
            onConfirm={handleNameConfirm}
            onClose={() => setShowNamePicker(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
